import React, { useState, useEffect } from 'react';
import {
  StandardTableActions,
  TableActionButton,
  TableActions,
  Button,
  Select,
  Badge,
  CopyIcon,
  DownloadIcon,
  CheckIcon,
  XIcon,
  ClockIcon,
  PaperAirplaneIcon,
} from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';
import { useNavigate } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { Offer, OfferLineItem } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import { exportOfferToPdf, OFFER_PDF_TEMPLATE_OPTIONS, type OfferPdfTemplateId } from '../../utils/offerPdfExport';
import type { OfferPdfTemplateDefinition } from '../../utils/offerPdfTypes';
import { useCompanyProfile, getCompanyLogoUrl } from '../../modules/settings/useData';
import api from '../../services/api';
import { useOfferPdfLayouts } from '../../modules/offer-pdf-layouts/useData';
import { cn } from '../../lib/utils';
import type { FieldConfig } from '../../components/generic/fieldRenderers';
import type { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { OfferLineItemsEditor, CatalogItemThumbnail } from './OfferLineItemsEditor';
import { useNotification } from '../../contexts/NotificationContext';
import { renderOfferFollowUpStatusBadge } from './OfferFollowUpsSection';
import { OfferFollowUpsSection } from './OfferFollowUpsSection';

/** Compute subtotal and total from line items and VAT rate (same logic as backend). Subtotal already includes line discounts; total = subtotal × (1 + vatRate/100). */
function computeSubtotalAndTotal(
  lineItems: Array<{ quantity: number; unitPrice: number; discount?: number; amount?: number }>,
  _discount: number,
  vatRate: number | null
): { subtotal: number; total: number } {
  const lineAmount = (item: (typeof lineItems)[0]) =>
    item.amount ?? Math.max(0, item.quantity * item.unitPrice - (item.discount ?? 0));
  const subtotal = lineItems.reduce((sum, item) => sum + lineAmount(item), 0);
  const total = vatRate != null ? subtotal * (1 + vatRate / 100) : subtotal;
  return { subtotal, total };
}

/** Compute validity text from offer date and valid-until (e.g. "15 days" or "1 day"). */
function computeValidityText(offerDate: string | null | undefined, validUntil: string | null | undefined): string {
  if (!offerDate || !validUntil) return '';
  const a = new Date(offerDate);
  const b = new Date(validUntil);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
  const diffMs = b.getTime() - a.getTime();
  const days = Math.round(diffMs / (24 * 60 * 60 * 1000));
  if (days < 0) return '';
  if (days === 1) return '1 day';
  return `${days} days`;
}

const fetchFullOffer = async (offer: Offer): Promise<Offer> => {
  const hasCatalogData = offer.OfferLineItem?.some((l) => l.catalogItem != null);
  if (hasCatalogData) return offer;
  try {
    const res = await api.get(`/offers/${offer.id}`);
    return res.data as Offer;
  } catch {
    return offer;
  }
};

const exportOfferPdf = async (
  offer: Offer,
  companyProfile: ReturnType<typeof useCompanyProfile>['companyProfile'],
  options: { templateId?: OfferPdfTemplateId; templateDefinition?: OfferPdfTemplateDefinition | null }
) => {
  const fullOffer = await fetchFullOffer(offer);
  const useCompanyLogo = !!companyProfile?.logoPath;
  const logoUrlOverride = useCompanyLogo ? getCompanyLogoUrl() : undefined;
  const logoRequiresAuth = !!useCompanyLogo;
  const companyDetails =
    companyProfile &&
    ({
      companyName: companyProfile.companyName,
      companyAddress: companyProfile.companyAddress,
      companyPhone: companyProfile.companyPhone,
      companyEmail: companyProfile.companyEmail,
      signatoryName: companyProfile.signatoryName,
    } as const);
  await exportOfferToPdf(fullOffer, {
    templateId: options.templateId,
    templateDefinition: options.templateDefinition,
    logoUrlOverride,
    logoRequiresAuth,
    companyDetails: companyDetails ?? undefined,
  });
};

const BUILT_IN_VALUES = ['classic', 'minimal', 'compact'] as const;

function isBuiltIn(value: string): value is OfferPdfTemplateId {
  return BUILT_IN_VALUES.includes(value as OfferPdfTemplateId);
}

const ExportPdfButton: React.FC<{ offer: Offer }> = ({ offer }) => {
  const [loading, setLoading] = useState(false);
  const { layouts } = useOfferPdfLayouts();
  const defaultLayout = layouts.find((l) => l.isDefault);
  const [selectedValue, setSelectedValue] = useState<string>(defaultLayout ? String(defaultLayout.id) : 'classic');
  const { companyProfile } = useCompanyProfile();

  useEffect(() => {
    if (defaultLayout && selectedValue === 'classic') {
      setSelectedValue(String(defaultLayout.id));
    }
  }, [defaultLayout?.id]);

  const options = React.useMemo(() => {
    const builtIn = OFFER_PDF_TEMPLATE_OPTIONS.map((o) => ({ value: o.value, label: `${o.label} (built-in)` }));
    if (layouts.length === 0) return builtIn;
    const saved = layouts.map((l) => ({ value: String(l.id), label: l.isDefault ? `${l.name} (default)` : l.name }));
    return [...builtIn, ...saved];
  }, [layouts]);

  const selectedLayout = React.useMemo(
    () => (selectedValue && !isBuiltIn(selectedValue) ? layouts.find((l) => String(l.id) === selectedValue) : null),
    [selectedValue, layouts]
  );

  const handleExport = async () => {
    setLoading(true);
    try {
      if (selectedLayout?.definition) {
        await exportOfferPdf(offer, companyProfile, { templateDefinition: selectedLayout.definition as OfferPdfTemplateDefinition });
      } else {
        await exportOfferPdf(offer, companyProfile, { templateId: (isBuiltIn(selectedValue) ? selectedValue : 'classic') as OfferPdfTemplateId });
      }
    } catch (e) {
      console.error('PDF export failed', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={selectedValue}
        onChange={(e) => setSelectedValue(e.target.value)}
        options={options}
        placeholder="Style"
        showClearButton={false}
        className="w-[180px]"
      />
      <Button
        type="button"
        variant="outline"
        onClick={handleExport}
        disabled={loading}
        loading={loading}
        className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
      >
        Export PDF
      </Button>
    </div>
  );
};

/** Icon-only export for table row actions. Uses default layout or Classic. */
const ExportPdfIconButton: React.FC<{ offer: Offer }> = ({ offer }) => {
  const [loading, setLoading] = useState(false);
  const { layouts } = useOfferPdfLayouts();
  const defaultLayout = layouts.find((l) => l.isDefault);
  const { companyProfile } = useCompanyProfile();
  const handleExport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      if (defaultLayout?.definition) {
        await exportOfferPdf(offer, companyProfile, { templateDefinition: defaultLayout.definition as OfferPdfTemplateDefinition });
      } else {
        await exportOfferPdf(offer, companyProfile, { templateId: 'classic' });
      }
    } catch (err) {
      console.error('PDF export failed', err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <TableActionButton
      title="Export PDF"
      tone="notify"
      onClick={handleExport}
      disabled={loading}
    >
      {loading ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <DownloadIcon className="h-4 w-4" />
      )}
    </TableActionButton>
  );
};

export const moduleConfig = {
  module: {
    title: 'Offers',
    description: 'Proposals and quotations',
    permissionModuleKey: 'offers',
    route: {
      list: '/offers',
      new: '/offers/new',
      detail: (id: number) => `/offers/${id}`,
      edit: (id: number) => `/offers/${id}/edit`,
    },
    api: {
      list: '/offers',
      detail: (id: number) => `/offers/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search by offer number, description or notes...',
  },
  filterGroups: [{ name: 'basic', label: 'Basic', icon: '🔍', fields: ['status', 'offerNumber', 'offerDate'] }],
  fields: [
    { name: 'status', label: 'Status', type: 'text' },
    { name: 'offerNumber', label: 'Offer number', type: 'text' },
    { name: 'offerDate', label: 'Date', type: 'date' },
  ],
  filters: {
    fields: [
      { key: 'status', label: 'Status', placeholder: 'All statuses', type: 'select' as const, getValue: (item: Offer) => item.status || 'draft', options: [{ value: '', label: 'All' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'accepted', label: 'Accepted' }, { value: 'declined', label: 'Declined' }, { value: 'expired', label: 'Expired' }] },
      { key: 'offerNumber', label: 'Offer number', placeholder: 'Offer #...', type: 'text' as const, getValue: (item: Offer) => item.offerNumber, options: [] },
      { key: 'offerDate', label: 'Date', placeholder: 'Date', type: 'date' as const, getValue: (item: Offer) => (item.offerDate ? formatDateGlobal(item.offerDate) : undefined), options: [] },
    ],
    applyFilter: (items: Offer[], filters: Record<string, string[] | string>) => {
      return items.filter((item) => {
        const statusFilter = filters.status;
        if (typeof statusFilter === 'string' && statusFilter.trim()) {
          const s = item.status || 'draft';
          if (s !== statusFilter.trim()) return false;
        } else if (Array.isArray(statusFilter) && statusFilter.length > 0) {
          const s = item.status || 'draft';
          if (!statusFilter.some((f) => s === String(f).trim())) return false;
        }
        const num = filters.offerNumber;
        if (typeof num === 'string' && num.trim()) {
          if (!(item.offerNumber || '').toLowerCase().includes(num.toLowerCase())) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      { key: 'add', icon: 'add', title: 'Add Offer', showTitle: false, variant: 'primary' as const, href: '/offers/new' },
    ],
  },
  table: {
    actionsPosition: 'right' as const,
    actionsInColumns: true,
    onRowClick: (item: Offer) => {
      window.location.href = `/offers/${item.id}`;
    },
    keyExtractor: (item: Offer) => item.id,
    getClientId: (item: Offer) => item.clientId ?? null,
  },
  emptyStates: {
    noData: 'Add an offer to get started.',
    noSearchResults: 'No offers match your search.',
    noFilterResults: 'No offers match the selected filters.',
  },
  delete: {
    title: 'Delete Offer',
    message: 'Are you sure you want to delete this offer?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete offer.',
    handler: async (id: number) => {
      const { deleteOffer } = await import('../../services/mutations');
      await deleteOffer(id);
    },
  },
  utils: {
    formatDate: (dateString?: string | null) => (dateString ? formatDateGlobal(dateString) : '-'),
    formatCurrency: (amount?: number | null) =>
      amount == null ? '-' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount),
  },
  detail: {
    subtitle: 'Offer Details',
    notFoundTitle: 'Offer not found',
    notFoundMessage: 'This offer does not exist.',
    notFoundBackText: 'Back to Offers',
    defaultTitle: 'Unnamed Offer',
    getTitle: (item: Offer) => item.offerNumber || `Offer #${item.id}`,
  },
  form: {
    new: { title: 'New Offer', subtitle: 'Create a proposal or quotation', submitText: 'Create Offer' },
    edit: { title: 'Edit Offer', subtitle: 'Update proposal', submitText: 'Update Offer' },
    cancelText: 'Cancel',
    loadingText: 'Loading offer...',
    errorMessage: 'Failed to save offer.',
    supportsFiles: true,
    create: async (data: Partial<Offer> & { templateId?: string; lineItems?: Array<{ name?: string; description?: string; model?: string; quantity: number; unitPrice: number; discount?: number; amount: number; catalogItemId?: number | null }> }, files?: File[], filesToDetach?: number[]) => {
      const { createOffer } = await import('../../services/mutations');
      const { templateId: _t, ...rest } = data as Record<string, unknown>;
      const payload: Record<string, unknown> = {
        ...rest,
        clientId: data.clientId != null && String(data.clientId) !== '' ? Number(data.clientId) : undefined,
        leadId: data.leadId != null && String(data.leadId) !== '' ? Number(data.leadId) : undefined,
        offerNumber: data.offerNumber && String(data.offerNumber).trim() ? String(data.offerNumber).trim() : undefined,
        offerDate: data.offerDate || new Date().toISOString().slice(0, 10),
        status: data.status ?? 'draft',
        subtotal: data.subtotal != null ? Number(data.subtotal) : undefined,
        discount: data.discount != null ? Number(data.discount) : undefined,
        vatRate: data.vatRate != null ? Number(data.vatRate) : undefined,
        total: data.total != null ? Number(data.total) : undefined,
        lineItems: (data as any).lineItems ?? [],
      };
      return createOffer(payload, files, filesToDetach) as Promise<Offer>;
    },
    update: async (id: number, data: Partial<Offer> & { templateId?: string; lineItems?: Array<{ name?: string; description?: string; model?: string; quantity: number; unitPrice: number; discount?: number; amount: number; catalogItemId?: number | null }> }, files?: File[], filesToDetach?: number[]) => {
      const { updateOffer } = await import('../../services/mutations');
      const { templateId: _t, ...rest } = data as Record<string, unknown>;
      const payload: Record<string, unknown> = {
        ...rest,
        clientId: data.clientId != null && String(data.clientId) !== '' ? Number(data.clientId) : undefined,
        leadId: data.leadId != null && String(data.leadId) !== '' ? Number(data.leadId) : undefined,
        subtotal: data.subtotal != null ? Number(data.subtotal) : undefined,
        discount: data.discount != null ? Number(data.discount) : undefined,
        vatRate: data.vatRate != null ? Number(data.vatRate) : undefined,
        total: data.total != null ? Number(data.total) : undefined,
        lineItems: (data as any).lineItems ?? [],
      };
      return updateOffer(id, payload, files, filesToDetach) as Promise<Offer>;
    },
    getViewFileUrl: (entityId: number, fileId: number) => `/offers/${entityId}/files/${fileId}`,
    disableWhen: (item: Offer) => item?.status === 'accepted' || item?.status === 'declined',
    disabledMessage: 'This offer has already been accepted or declined. Editing is disabled.',
  },
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
];

interface ClientDetail {
  clientName?: string | null;
  clientName2?: string | null;
  contactPerson?: string | null;
  clientAddress?: string | null;
  clientPhone?: string | null;
  clientPhone2?: string | null;
  clientEmail?: string | null;
  clientEmail2?: string | null;
}

export const offerFormFields: FieldConfig[] = [
  {
    name: 'clientId',
    label: 'Client',
    type: 'select',
    placeholder: 'Select client (optional)',
    options: [{ value: '', label: 'None' }],
    apiEndpoint: '/crm-clients',
    labelField: 'clientName',
    valueField: 'clientId' as const,
    runSideEffectOnClose: true,
    onChangeSideEffect: async (value, _setFieldValue, setMultipleFieldValues) => {
      if (value == null || value === '') {
        setMultipleFieldValues({
          companyName: '',
          companyAddress: '',
          companyPhone: '',
          companyEmail: '',
          signatoryName: '',
        });
        return;
      }
      const api = (await import('../../services/api')).default;
      const res = await api.get(`/crm-clients/${value}`);
      const client = res.data as ClientDetail;
      if (client) {
        setMultipleFieldValues({
          companyName: client.clientName ?? client.clientName2 ?? '',
          companyAddress: client.clientAddress ?? '',
          companyPhone: client.clientPhone ?? client.clientPhone2 ?? '',
          companyEmail: client.clientEmail ?? client.clientEmail2 ?? '',
          signatoryName: client.contactPerson ?? '',
        });
      }
    },
  },
  {
    name: 'leadId',
    label: 'Lead',
    type: 'select',
    placeholder: 'Select client first, then choose a lead',
    options: [{ value: '', label: 'None' }],
    optionsEndpointFromFormData: (formData) => {
      const cid = formData?.clientId;
      if (cid == null || cid === '') return null;
      const id = Number(cid);
      if (Number.isNaN(id)) return null;
      return `/crm-leads/all_of/${id}`;
    },
    optionsDependOnFields: ['clientId'],
    labelField: 'leadDescr',
    valueField: 'leadId' as const,
  },
  { name: 'offerNumber', label: 'Offer number', type: 'text', placeholder: 'Auto-generated if empty', helperText: 'Leave blank to auto-generate (e.g. 1/2026)' },
  { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Short description to identify the offer', rows: 2 },
  {
    name: 'offerDate',
    label: 'Offer date',
    type: 'date',
    placeholder: 'Select date',
    validation: { required: true },
    onChangeSideEffect: (_value, setFieldValue, _setMultipleFieldValues, getFormData) => {
      const form = getFormData?.();
      if (!form) return;
      const offerDateStr = form.offerDate;
      let validUntilStr = form.validUntil;
      if (offerDateStr && (!validUntilStr || validUntilStr === '')) {
        const d = new Date(offerDateStr);
        d.setDate(d.getDate() + 14);
        validUntilStr = d.toISOString().slice(0, 10);
        setFieldValue('validUntil', validUntilStr);
      }
      const text = computeValidityText(offerDateStr, validUntilStr || form.validUntil);
      setFieldValue('validityText', text);
    },
  },
  {
    name: 'validUntil',
    label: 'Valid until',
    type: 'date',
    placeholder: 'Select date',
    onChangeSideEffect: (_value, setFieldValue, _setMultipleFieldValues, getFormData) => {
      const form = getFormData?.();
      if (!form) return;
      const text = computeValidityText(form.offerDate, form.validUntil);
      setFieldValue('validityText', text);
    },
  },
  { name: 'validityText', label: 'Validity text', type: 'text', placeholder: 'e.g. 15 days' },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    placeholder: 'Status',
    options: [{ value: '', label: 'Select...' }, ...STATUS_OPTIONS],
    onChangeSideEffect: (newStatus, setFieldValue, setMultipleFieldValues, getFormData) => {
      const form = getFormData?.();
      if (!form || !newStatus) return;
      const statusToDateField: Record<string, string> = {
        sent: 'sendDate',
        accepted: 'acceptedDate',
        declined: 'declinedDate',
        expired: 'expiredDate',
      };
      const dateField = statusToDateField[String(newStatus)];
      if (!dateField) return;
      const existingDate = form[dateField];
      if (existingDate && String(existingDate).trim() !== '') {
        const msg = `Update ${String(newStatus)} date to today? (Current: ${existingDate})`;
        if (!window.confirm(msg)) return;
      }
      const today = new Date().toISOString().slice(0, 10);
      setFieldValue(dateField, today);
    },
  },
  {
    name: 'totalValue',
    label: 'Gross Amount',
    type: 'custom',
    customRender: ({ value }) => (
      <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-right font-medium tabular-nums">
        {value != null ? moduleConfig.utils.formatCurrency(Number(value)) : '-'}
      </div>
    ),
    helperText: '(before discounts)',
  },
  {
    name: 'discount',
    label: 'Discount Amount',
    type: 'custom',
    customRender: ({ value }) => (
      <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-right font-medium tabular-nums">
        {value != null ? moduleConfig.utils.formatCurrency(Number(value)) : '-'}
      </div>
    ),
  },
  {
    name: 'subtotal',
    label: 'Net Amount',
    type: 'custom',
    customRender: ({ value }) => (
      <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-right font-medium tabular-nums">
        {value != null ? moduleConfig.utils.formatCurrency(Number(value)) : '-'}
      </div>
    ),
    helperText: '(before VAT)',
  },
  {
    name: 'vatRate',
    label: 'VAT rate (%)',
    type: 'number',
    placeholder: '24',
    min: 0,
    helperText: 'Change to recalculate total',
    onChangeSideEffect: (newVatRate, _setFieldValue, setMultipleFieldValues, getFormData) => {
      const form = getFormData?.() ?? {};
      const subtotal = Number(form.subtotal) || 0;
      const vatRate = newVatRate != null && newVatRate !== '' ? Number(newVatRate) : null;
      const total = vatRate != null ? subtotal * (1 + vatRate / 100) : subtotal;
      setMultipleFieldValues({ total });
    },
  },
  {
    name: 'total',
    label: 'Grand Total',
    type: 'custom',
    customRender: ({ value }) => (
      <div className="px-3 py-2 rounded-md border border-indigo-300 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-sm text-right font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
        {value != null ? moduleConfig.utils.formatCurrency(Number(value)) : '-'}
      </div>
    ),
  },
  { name: 'companyName', label: 'Company name', type: 'text', placeholder: 'Company name' },
  { name: 'companyAddress', label: 'Address', type: 'textarea', placeholder: 'Address', rows: 2 },
  { name: 'companyPhone', label: 'Phone', type: 'text', placeholder: 'Phone' },
  { name: 'companyEmail', label: 'Email', type: 'text', placeholder: 'Email' },
  { name: 'signatoryName', label: 'Signatory', type: 'text', placeholder: 'Signatory name' },
  { name: 'draftDate', label: 'Draft date', type: 'date', placeholder: '—' },
  { name: 'sendDate', label: 'Sent date', type: 'date', placeholder: '—' },
  { name: 'acceptedDate', label: 'Accepted date', type: 'date', placeholder: '—' },
  { name: 'declinedDate', label: 'Declined date', type: 'date', placeholder: '—' },
  { name: 'expiredDate', label: 'Expired date', type: 'date', placeholder: '—' },
  {
    name: 'templateId',
    label: 'Apply template',
    type: 'select',
    placeholder: 'Select template to copy terms...',
    options: [{ value: '', label: 'None' }],
    apiEndpoint: '/offer-templates',
    labelField: 'name',
    valueField: 'id' as const,
    helperText: 'Copy intro, delivery, payment and notes from a template.',
    onChangeSideEffect: async (value, _setFieldValue, setMultipleFieldValues) => {
      if (value == null || value === '') return;
      const id = typeof value === 'string' ? parseInt(value, 10) : value;
      if (Number.isNaN(id)) return;
      const api = (await import('../../services/api')).default;
      const res = await api.get(`/offer-templates/${id}`);
      const t = res.data as { introText?: string; deliveryText?: string; paymentMethodText?: string; notes?: string };
      if (t) {
        setMultipleFieldValues({
          introText: t.introText ?? '',
          deliveryText: t.deliveryText ?? '',
          paymentMethodText: t.paymentMethodText ?? '',
          notes: t.notes ?? '',
        });
      }
    },
  },
  { name: 'introText', label: 'Intro text', type: 'textarea', placeholder: 'Introduction', rows: 3 },
  { name: 'deliveryText', label: 'Delivery', type: 'text', placeholder: 'e.g. Delivery in 1-3 days' },
  { name: 'paymentMethodText', label: 'Payment method', type: 'text', placeholder: 'Payment method' },
  { name: 'notes', label: 'Notes / bank details', type: 'textarea', placeholder: 'Notes', rows: 4 },
  {
    name: 'lineItems',
    label: '',
    type: 'custom',
    customRender: ({ value, onChange }) => (
      <OfferLineItemsEditor
        items={value ?? []}
        onChange={onChange}
        formatCurrency={moduleConfig.utils.formatCurrency}
      />
    ),
    onChangeSideEffect: (newItems, _setFieldValue, setMultipleFieldValues, getFormData) => {
      const items = Array.isArray(newItems) ? newItems : [];
      const totalValue = items.reduce((sum, item) => sum + (item.quantity ?? 0) * (item.unitPrice ?? 0), 0);
      const discount = items.reduce((sum, item) => sum + (item.discount ?? 0), 0);
      const vatRate = getFormData?.()?.vatRate;
      const vat = vatRate != null ? Number(vatRate) : null;
      const { subtotal, total } = computeSubtotalAndTotal(items, discount, vat);
      setMultipleFieldValues({ totalValue, discount, subtotal, total });
    },
  },
];

export const offerFormLayout: FormLayout = {
  mode: 'default',
  sections: [
    {
      id: 'offer',
      title: 'Offer information',
      icon: '📄',
      fields: ['clientId', 'offerNumber', 'leadId', 'templateId', 'description', 'offerDate', 'validUntil', 'validityText', 'status', 'signatoryName'],
      collapsible: false,
      gridCols: 'md:grid-cols-12',
      fieldSpans: { clientId: 'md:col-span-6', leadId: 'md:col-span-6', templateId: 'md:col-span-6', offerNumber: 'md:col-span-6', description: 'md:col-span-12', offerDate: 'md:col-span-6', validUntil: 'md:col-span-6', validityText: 'md:col-span-6', status: 'md:col-span-6', signatoryName: 'md:col-span-12' },
    },
    { id: 'lines', title: 'Line items', icon: '📦', fields: ['lineItems'], collapsible: false, gridCols: 'md:grid-cols-1', fieldSpans: { lineItems: 'w-full md:col-span-12' } },
    {
      id: 'totals',
      title: 'Totals',
      icon: '💰',
      fields: ['totalValue', 'discount', 'subtotal', 'vatRate', 'total'],
      collapsible: false,
      gridCols: 'md:grid-cols-12',
      fieldSpans: { totalValue: 'md:col-span-2', discount: 'md:col-span-2', subtotal: 'md:col-span-3', vatRate: 'md:col-span-2', total: 'md:col-span-3' },
    },
    { id: 'terms', title: 'Terms', icon: '📋', fields: ['introText', 'deliveryText', 'paymentMethodText', 'notes'], collapsible: false, gridCols: 'md:grid-cols-12', fieldSpans: { introText: 'md:col-span-12', deliveryText: 'md:col-span-6', paymentMethodText: 'md:col-span-6', notes: 'md:col-span-12' } },
    { id: 'company', title: 'Company / letterhead', icon: '🏢', fields: ['companyName', 'companyAddress', 'companyPhone', 'companyEmail'], collapsible: true, defaultCollapsed: true, toggleable: true, defaultDisabled: true, gridCols: 'md:grid-cols-12', fieldSpans: { companyName: 'md:col-span-12', companyAddress: 'md:col-span-12', companyPhone: 'md:col-span-6', companyEmail: 'md:col-span-6' } },
    {
      id: 'statusDates',
      title: 'Status dates',
      icon: '📅',
      fields: ['draftDate', 'sendDate', 'acceptedDate', 'declinedDate', 'expiredDate'],
      collapsible: false,
      gridCols: 'md:grid-cols-12',
      fieldSpans: { draftDate: 'md:col-span-6', sendDate: 'md:col-span-6', acceptedDate: 'md:col-span-6', declinedDate: 'md:col-span-6', expiredDate: 'md:col-span-6' },
    },
  ],
};

export type OfferFormData = Partial<Omit<Offer, 'clientId' | 'leadId'>> & {
  clientId?: string | number;
  leadId?: string | number;
  /** Form-only: select template to copy terms; not sent to API */
  templateId?: string | number;
  totalValue?: number;
  lineItems?: Array<{ name?: string; description?: string; model?: string; quantity: number; unitPrice: number; discount?: number; amount: number; catalogItemId?: number | null }>;
};

export const getInitialFormData = (): OfferFormData => ({
  clientId: '',
  leadId: '',
  offerNumber: '',
  description: '',
  offerDate: new Date().toISOString().slice(0, 10),
  validUntil: '',
  validityText: '',
  status: 'draft',
  totalValue: undefined,
  subtotal: undefined,
  discount: undefined,
  vatRate: 24,
  total: undefined,
  companyName: '',
  companyAddress: '',
  companyPhone: '',
  companyEmail: '',
  signatoryName: '',
  draftDate: '',
  sendDate: '',
  acceptedDate: '',
  declinedDate: '',
  expiredDate: '',
  templateId: '',
  introText: '',
  deliveryText: '',
  paymentMethodText: '',
  notes: '',
  lineItems: [],
});

export const mapItemToFormData = (item: Offer): OfferFormData => {
  const lineItems = item.OfferLineItem || [];
  const discountFromLines = lineItems.reduce((sum, l) => sum + (l.discount ?? 0), 0);
  const totalValueFromLines = lineItems.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);
  return {
  clientId: item.clientId != null ? String(item.clientId) : '',
  leadId: item.leadId != null ? String(item.leadId) : '',
  offerNumber: item.offerNumber ?? '',
  description: item.description ?? '',
  offerDate: item.offerDate ? item.offerDate.slice(0, 10) : '',
  validUntil: item.validUntil ? item.validUntil.slice(0, 10) : '',
  validityText: item.validityText ?? '',
  status: item.status ?? 'draft',
  totalValue: lineItems.length > 0 ? totalValueFromLines : undefined,
  subtotal: item.subtotal ?? undefined,
  discount: lineItems.length > 0 ? discountFromLines : (item.discount ?? undefined),
  vatRate: item.vatRate ?? 24,
  total: item.total ?? undefined,
  companyName: item.companyName ?? '',
  companyAddress: item.companyAddress ?? '',
  companyPhone: item.companyPhone ?? '',
  companyEmail: item.companyEmail ?? '',
  signatoryName: item.signatoryName ?? '',
  draftDate: item.draftDate ? item.draftDate.slice(0, 10) : '',
  sendDate: item.sendDate ? item.sendDate.slice(0, 10) : '',
  acceptedDate: item.acceptedDate ? item.acceptedDate.slice(0, 10) : '',
  declinedDate: item.declinedDate ? item.declinedDate.slice(0, 10) : '',
  expiredDate: item.expiredDate ? item.expiredDate.slice(0, 10) : '',
  templateId: '',
  introText: item.introText ?? '',
  deliveryText: item.deliveryText ?? '',
  paymentMethodText: item.paymentMethodText ?? '',
  notes: item.notes ?? '',
  lineItems: lineItems.map((l) => ({ name: l.name ?? '', description: l.description ?? '', model: l.model ?? '', quantity: l.quantity, unitPrice: l.unitPrice, discount: l.discount ?? 0, amount: l.amount, catalogItemId: l.catalogItemId ?? null })),
  };
};

export function getClientDisplayName(offer: Offer): string {
  if (offer.client?.clientName) return offer.client.clientName;
  if (offer.client?.clientName2) return offer.client.clientName2;
  if (offer.lead?.leadDescr) return offer.lead.leadDescr;
  return '-';
}

type OffersListContext = {
  mutate?: () => void;
  showError?: (msg: string) => void;
  onDelete?: (id: number) => void;
  tableActionsOptions?: { canDelete?: boolean; canEdit?: boolean };
  onOpenFollowUpsPanel?: (item: Offer) => void;
};

function getRowActions(
  item: Offer,
  onDelete: (id: number) => void,
  _colors?: ThemeColors,
  options?: { canDelete?: boolean; canEdit?: boolean },
  listContext?: OffersListContext
): React.ReactNode {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  const handleDuplicate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { duplicateOffer } = await import('../../services/mutations');
      const newOffer = await duplicateOffer(item.id);
      listContext?.mutate?.();
      window.location.href = `/offers/${newOffer.id}/edit`;
    } catch (_) {
      // Error handled by mutation/global handler
    }
  };
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    >
      <ExportPdfIconButton offer={item} />
      <TableActionButton title="Duplicate offer" onClick={handleDuplicate}>
        <CopyIcon className="h-4 w-4" />
      </TableActionButton>
    </StandardTableActions>
  );
}

function getStatusActions(item: Offer, listContext?: OffersListContext): React.ReactNode {
  const mutate = listContext?.mutate;
  const showError = listContext?.showError;
  const setStatusWithDate = async (
    status: 'sent' | 'accepted' | 'declined' | 'expired',
    dateField: 'sendDate' | 'acceptedDate' | 'declinedDate' | 'expiredDate'
  ) => {
    try {
      const { updateOffer } = await import('../../services/mutations');
      const today = new Date().toISOString();
      await updateOffer(item.id, { status, [dateField]: today });
      mutate?.();
    } catch (e) {
      showError?.(e instanceof Error ? e.message : 'Failed to update offer');
    }
  };
  return (
    <TableActions>
      {item.status === 'draft' && (
        <TableActionButton
          title="Mark as sent"
          tone="edit"
          onClick={() => {
            void setStatusWithDate('sent', 'sendDate');
          }}
        >
          <PaperAirplaneIcon className="h-4 w-4" />
        </TableActionButton>
      )}
      {(item.status === 'draft' || item.status === 'sent') && (
        <>
          <TableActionButton
            title="Accept offer"
            tone="notify"
            onClick={() => {
              void setStatusWithDate('accepted', 'acceptedDate');
            }}
          >
            <CheckIcon className="h-4 w-4" />
          </TableActionButton>
          <TableActionButton
            title="Decline offer"
            tone="delete"
            onClick={() => {
              void setStatusWithDate('declined', 'declinedDate');
            }}
          >
            <XIcon className="h-4 w-4" />
          </TableActionButton>
          <TableActionButton
            title="Mark as expired"
            tone="summary"
            onClick={() => {
              void setStatusWithDate('expired', 'expiredDate');
            }}
          >
            <ClockIcon className="h-4 w-4" />
          </TableActionButton>
        </>
      )}
    </TableActions>
  );
}

export const getColumns = (
  formatDate: (dateString?: string) => string,
  formatCurrency?: (amount?: number | null) => string,
  colors?: ThemeColors,
  listContext?: OffersListContext
): Column<Offer>[] => {
  const fmt = formatCurrency || moduleConfig.utils.formatCurrency;
  const text = colors?.text || 'text-gray-900';
  const muted = colors?.textMuted || 'text-gray-500';
  const onDelete = listContext?.onDelete ?? (() => {});
  const options = listContext?.tableActionsOptions ?? { canDelete: true, canEdit: true };
  const baseColumns: Column<Offer>[] = [
    {
      key: 'offerNumber',
      header: 'Offer',
      sortable: true,
      render: (_, row) => (
        <div className={`text-sm font-medium ${text}`}>{row.offerNumber}</div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      sortable: false,
      render: (_, row) => {
        const clientLine = getClientDisplayName(row);
        if (clientLine === '-') {
          return (
            <div className={`text-sm ${text} max-w-[200px] truncate`} title={row.description ?? undefined}>
              {row.description || '—'}
            </div>
          );
        }
        return (
          <div className="min-w-0 max-w-[200px]">
            <div className={`text-sm ${text} truncate`} title={row.description ?? undefined}>
              {row.description || '—'}
            </div>
            <div className={`text-xs ${muted} truncate mt-0.5`}>{clientLine}</div>
          </div>
        );
      },
    },
    {
      key: 'offerDate',
      header: 'Dates',
      sortable: true,
      render: (_, row) => {
        return (
          <div className="min-w-0">
            <div className={`text-sm ${text}`}>{formatDate(row.offerDate ?? undefined)}</div>
            <div className={`text-xs ${muted}`}>
              {row.validUntil ? `Valid: ${formatDate(row.validUntil ?? undefined)}` : 'Valid: -'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_, row) => {
        const s = (row.status || 'draft') as 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';
        const statusDate =
          s === 'draft' ? row.draftDate
          : s === 'sent' ? row.sendDate
          : s === 'accepted' ? row.acceptedDate
          : s === 'declined' ? row.declinedDate
          : s === 'expired' ? row.expiredDate
          : null;
        const inv = row.clientInvoice;
        const invLabel = s === 'accepted' && inv ? (inv.InvoiceNo ?? `#${inv.invoiceId}`) : null;
        const flowLabels =
          s === 'draft' ? ['Draft']
          : s === 'sent' ? ['Draft', 'Send']
          : ['Draft', 'Send', s === 'accepted' ? 'Accepted' : s === 'declined' ? 'Declined' : 'Expired'];
        const currentVariant: BadgeVariant =
          s === 'accepted' ? 'success'
          : s === 'declined' ? 'danger'
          : s === 'expired' ? 'warning'
          : s === 'sent' ? 'info'
          : 'secondary';
        return (
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-0.5 text-xs">
              {flowLabels.map((label, i) => (
                <span key={i} className="inline-flex items-center gap-0.5">
                  {i > 0 && <span className={muted}>→</span>}
                  <Badge
                    variant={i === flowLabels.length - 1 ? currentVariant : 'secondary'}
                    className="px-1.5 py-0.5"
                  >
                    {label}
                  </Badge>
                </span>
              ))}
            </div>
            <div className={`text-xs ${muted} mt-1 truncate`}>
              {statusDate ? formatDate(statusDate) : '—'}
            </div>
            {invLabel && (
              <div className={`text-xs ${muted} truncate`}>Inv: {invLabel}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'sendDate',
      header: 'Sent',
      sortable: true,
      render: (_, row) => (
        <div className={`text-sm ${text}`}>{row.sendDate ? formatDate(row.sendDate) : '—'}</div>
      ),
    },
    {
      key: 'acceptedDate',
      header: 'Accepted',
      sortable: true,
      render: (_, row) => (
        <div className={`text-sm ${text}`}>{row.acceptedDate ? formatDate(row.acceptedDate) : '—'}</div>
      ),
    },
    {
      key: 'total',
      header: 'Summary',
      sortable: true,
      render: (_, row) => (
        <div className="min-w-0">
          <div className={`text-sm ${text} font-semibold`}>{fmt(row.total)}</div>
          <div className={`text-xs ${muted}`}>
            {(row.OfferLineItem?.length ?? 0)} item(s) • VAT {row.vatRate != null ? `${Number(row.vatRate)}%` : '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'followUps',
      header: 'Follow-ups',
      sortable: false,
      render: (_value, item) => {
        const followUps = item.followUps ?? [];
        const count = followUps.length;
        const last = followUps[0];
        const lastDate = last?.contactedAt ? formatDate(last.contactedAt) : null;
        const lastStatusValue = last?.responseStatus ?? null;
        const responseDate = last?.responseAt ? formatDate(last.responseAt) : null;
        const onOpenPanel = listContext?.onOpenFollowUpsPanel;
        if (count === 0) {
          if (onOpenPanel) {
            return (
              <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenPanel(item); }} className="text-indigo-600 hover:text-indigo-800 hover:underline text-left">
                0 follow-ups
              </button>
            );
          }
          return '—';
        }
        const responseLine = lastStatusValue ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {renderOfferFollowUpStatusBadge(lastStatusValue)}
            {responseDate && <span className={cn('text-xs', muted)}>{responseDate}</span>}
          </div>
        ) : (
          <div className={cn('text-xs', muted)}>—</div>
        );
        const contactedLine = lastDate && (
          <div className={cn('text-xs mt-0.5', muted)}>Contacted {lastDate} · {count} follow-up{count !== 1 ? 's' : ''}</div>
        );
        if (onOpenPanel) {
          return (
            <div className="text-left">
              <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenPanel(item); }} className="text-left w-full hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded px-1 -mx-1 py-0.5">
                {responseLine}
                {contactedLine}
              </button>
            </div>
          );
        }
        return (
          <div className="text-left">
            {responseLine}
            {contactedLine}
          </div>
        );
      },
    },
  ];
  if (listContext) {
    return [
      {
        key: 'actions',
        header: 'Actions',
        sortable: false,
        align: 'right' as const,
        render: (_, row) => getRowActions(row, onDelete, colors, options, listContext),
      },
      ...baseColumns,
      {
        key: 'statusActions',
        header: 'Status actions',
        sortable: false,
        align: 'right' as const,
        render: (_, row) => getStatusActions(row, listContext),
      },
    ];
  }
  return baseColumns;
};

/** Status action buttons for detail page; calls onUpdated (e.g. mutate) after update */
function DetailStatusActions({ offer, onUpdated }: { offer: Offer; onUpdated?: () => void }) {
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  const setStatusWithDate = async (
    status: 'sent' | 'accepted' | 'declined' | 'expired',
    dateField: 'sendDate' | 'acceptedDate' | 'declinedDate' | 'expiredDate'
  ) => {
    if (loading) return;
    setLoading(true);
    try {
      const { updateOffer } = await import('../../services/mutations');
      const today = new Date().toISOString();
      await updateOffer(offer.id, { status, [dateField]: today });
      onUpdated?.();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to update offer');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {offer.status === 'draft' && (
        <Button type="button" onClick={() => setStatusWithDate('sent', 'sendDate')} disabled={loading} variant="outline" className="text-blue-600 border-blue-300">
          Send
        </Button>
      )}
      {(offer.status === 'draft' || offer.status === 'sent') && (
        <>
          <Button type="button" onClick={() => setStatusWithDate('accepted', 'acceptedDate')} disabled={loading} variant="outline" className="text-green-600 border-green-300">
            Accept
          </Button>
          <Button type="button" onClick={() => setStatusWithDate('declined', 'declinedDate')} disabled={loading} variant="outline" className="text-red-600 border-red-300">
            Decline
          </Button>
          <Button type="button" onClick={() => setStatusWithDate('expired', 'expiredDate')} disabled={loading} variant="outline" className="text-amber-600 border-amber-300">
            Expire
          </Button>
        </>
      )}
    </div>
  );
}

/** Duplicate offer button for detail page */
function DetailDuplicateButton({ offer, onDuplicated }: { offer: Offer; onDuplicated?: (newId: number) => void }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const handleDuplicate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { duplicateOffer } = await import('../../services/mutations');
      const newOffer = await duplicateOffer(offer.id);
      onDuplicated?.(newOffer.id);
      navigate(`/offers/${newOffer.id}/edit`);
    } catch (_) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button type="button" onClick={handleDuplicate} disabled={loading} variant="outline">
      Duplicate offer
    </Button>
  );
}

export const getDetailSections = (
  item: Offer,
  formatDate: (dateString?: string | null) => string,
  formatCurrency: (amount?: number | null) => string,
  mutate?: () => void
) => {
  const sections: Array<{ title: string; icon: string; fields: Array<{ label: string; value: React.ReactNode }> }> = [
    {
      title: 'Actions',
      icon: '📄',
      fields: [
        { label: '', value: <ExportPdfButton offer={item} /> },
        ...(mutate ? [{ label: '', value: <DetailDuplicateButton offer={item} onDuplicated={() => mutate()} /> }] : []),
      ],
    },
    ...(mutate
      ? [
          {
            title: 'Status actions',
            icon: '🔄',
            fields: [{ label: '', value: <DetailStatusActions offer={item} onUpdated={mutate} /> }],
          },
        ]
      : []),
    {
      title: 'Status history',
      icon: '📅',
      fields: [
        { label: 'Draft', value: formatDate(item.draftDate) || '—' },
        { label: 'Sent', value: formatDate(item.sendDate) || '—' },
        { label: 'Accepted', value: formatDate(item.acceptedDate) || '—' },
        { label: 'Declined', value: formatDate(item.declinedDate) || '—' },
        { label: 'Expired', value: formatDate(item.expiredDate) || '—' },
      ],
    },
    {
      title: 'Offer Information',
      icon: '📄',
      fields: [
        { label: 'Offer number', value: item.offerNumber },
        { label: 'Description', value: <span className="whitespace-pre-wrap">{item.description || '—'}</span> },
        { label: 'Client', value: getClientDisplayName(item) },
        { label: 'Date', value: formatDate(item.offerDate) },
        { label: 'Valid until', value: formatDate(item.validUntil) },
        { label: 'Validity', value: item.validityText || '-' },
        { label: 'Status', value: item.status || 'draft' },
        { label: 'Draft date', value: formatDate(item.draftDate) },
        { label: 'Sent date', value: formatDate(item.sendDate) },
        { label: 'Accepted date', value: formatDate(item.acceptedDate) },
        { label: 'Declined date', value: formatDate(item.declinedDate) },
        { label: 'Expired date', value: formatDate(item.expiredDate) },
        { label: 'Subtotal', value: formatCurrency(item.subtotal) },
        { label: 'Discount', value: formatCurrency(item.discount) },
        { label: 'VAT rate (%)', value: item.vatRate != null ? `${Number(item.vatRate)}` : '—' },
        { label: 'Total', value: formatCurrency(item.total) },
      ],
    },
    {
      title: 'Company / Letterhead',
      icon: '🏢',
      fields: [
        { label: 'Company name', value: item.companyName || '-' },
        { label: 'Address', value: item.companyAddress || '-' },
        { label: 'Phone', value: item.companyPhone || '-' },
        { label: 'Email', value: item.companyEmail || '-' },
        { label: 'Signatory', value: item.signatoryName || '-' },
      ],
    },
    {
      title: 'Terms',
      icon: '📋',
      fields: [
        { label: 'Intro', value: item.introText || '-' },
        { label: 'Delivery', value: item.deliveryText || '-' },
        { label: 'Payment', value: item.paymentMethodText || '-' },
        { label: 'Notes / Bank', value: item.notes || '-' },
      ],
    },
  ];
  if (item.OfferLineItem && item.OfferLineItem.length > 0) {
    sections.push({
      title: 'Line Items',
      icon: '📦',
      fields: [
        {
          label: '',
          value: (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 w-16">Image</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Product</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">Unit price</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">Discount</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {item.OfferLineItem.map((line: OfferLineItem) => {
                    const cat = line.catalogItem;
                    const attachments = cat?.FileAttachment ?? [];
                    const primaryId = cat?.primaryFileAttachmentId;
                    const chosenFile = primaryId ? attachments.find((f) => f.id === primaryId) : attachments[0];
                    const firstFile = chosenFile ?? attachments[0];
                    const isImage = firstFile?.fileType?.toLowerCase().startsWith('image/');
                    return (
                      <tr key={line.id ?? line.sortOrder} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="px-3 py-2 align-middle">
                          <div className="relative w-12 h-12 flex-shrink-0">
                            {firstFile && cat?.id && isImage ? (
                              <CatalogItemThumbnail
                                catalogItemId={cat.id}
                                fileId={firstFile.id}
                                className="w-12 h-12 object-cover rounded border border-gray-200 dark:border-gray-600 absolute inset-0"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded border border-dashed flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-400 absolute inset-0" aria-hidden>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">{line.name || '-'}</div>
                          {line.description && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{line.description}</div>}
                        </td>
                        <td className="px-3 py-2 text-sm text-right">{line.quantity}</td>
                        <td className="px-3 py-2 text-sm text-right">{formatCurrency(line.unitPrice)}</td>
                        <td className="px-3 py-2 text-sm text-right">{formatCurrency(line.discount ?? 0)}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium">{formatCurrency(line.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ),
        },
      ],
    });
  }
  sections.push({
    title: 'Follow-ups',
    icon: '🔔',
    fields: [{ label: '', value: React.createElement(OfferFollowUpsSection, { offerId: item.id, variant: 'panel' }) }],
  });
  sections.push({
    title: 'Attachments',
    icon: '📎',
    fields: [
      {
        label: '',
        value: React.createElement(EntityAttachmentsSection, {
          downloadEndpoint: `/offers/${item.id}/files`,
          files: item.FileAttachment || [],
          defaultDownloadName: `offer_${item.id}_files.zip`,
          getViewFileUrl: (fileId: number) => `/offers/${item.id}/files/${fileId}`,
        }),
      },
    ],
  });
  return sections;
};

export const getStatusBadges = (item: Offer): React.ReactNode => {
  const s = item.status || 'draft';
  const variant: BadgeVariant =
    s === 'accepted' ? 'success'
    : s === 'declined' ? 'danger'
    : s === 'sent' ? 'info'
    : 'secondary';
  return <Badge variant={variant}>{s}</Badge>;
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add')
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  return null;
};

export const getTableActions = (
  item: Offer,
  onDelete: (id: number) => void,
  colors?: ThemeColors,
  listContext?: OffersListContext,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  return getRowActions(item, onDelete, colors, options, listContext);
};

